/**
 * Admin Paygate page — configure payment gateway credentials (PayPal, Stripe).
 * When a gateway is enabled, the public /activation page shows "Buy" buttons
 * and lets the buyer pick which gateway to pay through.
 *
 * Credentials are stored server-side and NEVER exposed on the public API —
 * only `enabled` flags are surfaced through /api/public/paygate-status.
 */
import React, { useEffect, useState } from "react";
import { getPaygateConfig, updatePaygateConfig } from "../lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  CreditCard,
  Save,
  RefreshCw,
  ShieldCheck,
  Eye,
  EyeOff,
} from "lucide-react";

export default function Paygate() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPaygateConfig();
      setCfg(data);
      setInitial(data);
    } catch (e) {
      toast({
        title: "Failed to load paygate config",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty =
    cfg && initial && JSON.stringify(cfg) !== JSON.stringify(initial);
  const setField = (k, v) => setCfg((c) => ({ ...(c || {}), [k]: v }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const updated = await updatePaygateConfig(cfg);
      setCfg(updated);
      setInitial(updated);
      toast({
        title: "Paygate updated",
        description: "The public /activation page now reflects these gateways.",
      });
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setCfg(initial);

  if (loading || !cfg) {
    return (
      <div className="p-6 text-neutral-500" data-testid="paygate-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-paygate-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            <CreditCard className="h-7 w-7 text-emerald-400" />
            Paygate
          </h1>
          <p className="max-w-2xl text-sm text-neutral-400">
            Configure payment providers used on the public{" "}
            <a
              href="/activation"
              target="_blank"
              rel="noreferrer"
              className="text-emerald-400 underline"
              data-testid="paygate-view-activation"
            >
              /activation
            </a>{" "}
            page. When at least one gateway is enabled, buyers see a{" "}
            <span className="text-emerald-400">Buy</span> button and can pick
            their preferred method.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSecrets((v) => !v)}
            data-testid="paygate-toggle-secrets"
          >
            {showSecrets ? (
              <EyeOff className="mr-2 h-4 w-4" />
            ) : (
              <Eye className="mr-2 h-4 w-4" />
            )}
            {showSecrets ? "Hide secrets" : "Show secrets"}
          </Button>
          <Button
            variant="outline"
            onClick={load}
            disabled={saving}
            data-testid="paygate-refresh-btn"
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            Reload
          </Button>
          <Button
            variant="outline"
            onClick={revert}
            disabled={!dirty || saving}
            data-testid="paygate-revert-btn"
          >
            Revert
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="paygate-save-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* PayPal */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-[#003087] text-[10px] font-bold text-white">
                  PP
                </span>
                PayPal
              </CardTitle>
              <CardDescription className="text-xs text-neutral-500">
                Toggle on after filling in your PayPal REST API credentials
                (Business account → Developer Dashboard).
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="paypal_enabled" className="text-sm">
                Enabled
              </Label>
              <Switch
                id="paypal_enabled"
                checked={!!cfg.paypal_enabled}
                onCheckedChange={(v) => setField("paypal_enabled", !!v)}
                data-testid="paygate-paypal-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-xs text-neutral-400">Environment</Label>
              <Select
                value={cfg.paypal_environment || "sandbox"}
                onValueChange={(v) => setField("paypal_environment", v)}
              >
                <SelectTrigger
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid="paygate-paypal-env"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                  <SelectItem value="live">Live (production)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-neutral-400">Currency</Label>
              <Input
                value={cfg.paypal_currency || "USD"}
                onChange={(e) => setField("paypal_currency", e.target.value.toUpperCase())}
                placeholder="USD"
                className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                data-testid="paygate-paypal-currency"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-neutral-400">Business email</Label>
            <Input
              value={cfg.paypal_business_email || ""}
              onChange={(e) => setField("paypal_business_email", e.target.value)}
              placeholder="you@business.com"
              className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
              data-testid="paygate-paypal-business-email"
            />
          </div>

          <div>
            <Label className="text-xs text-neutral-400">Client ID</Label>
            <Input
              value={cfg.paypal_client_id || ""}
              onChange={(e) => setField("paypal_client_id", e.target.value)}
              placeholder="AY-…"
              className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-500"
              data-testid="paygate-paypal-client-id"
            />
          </div>

          <div>
            <Label className="text-xs text-neutral-400">Client Secret</Label>
            <Input
              type={showSecrets ? "text" : "password"}
              value={cfg.paypal_client_secret || ""}
              onChange={(e) => setField("paypal_client_secret", e.target.value)}
              placeholder="EL-…"
              className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-500"
              data-testid="paygate-paypal-client-secret"
            />
          </div>

          <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs text-blue-100/80">
            <p className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-blue-400" />
              <span>
                Get credentials from{" "}
                <a
                  href="https://developer.paypal.com/dashboard/applications"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-300 underline hover:text-blue-200"
                >
                  developer.paypal.com → My Apps
                </a>
                . Use <em>Sandbox</em> credentials until you&apos;ve tested a
                full checkout, then swap to <em>Live</em>.
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stripe */}
      <Card className="border-white/10 bg-neutral-900 opacity-90">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-[#635BFF] text-[10px] font-bold text-white">
                  S
                </span>
                Stripe
              </CardTitle>
              <CardDescription className="text-xs text-neutral-500">
                Card checkout via Stripe. Enable after filling in your
                Publishable + Secret keys.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="stripe_enabled" className="text-sm">
                Enabled
              </Label>
              <Switch
                id="stripe_enabled"
                checked={!!cfg.stripe_enabled}
                onCheckedChange={(v) => setField("stripe_enabled", !!v)}
                data-testid="paygate-stripe-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <Label className="text-xs text-neutral-400">Currency</Label>
            <Input
              value={cfg.stripe_currency || "USD"}
              onChange={(e) => setField("stripe_currency", e.target.value.toUpperCase())}
              placeholder="USD"
              className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
              data-testid="paygate-stripe-currency"
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Publishable Key</Label>
            <Input
              value={cfg.stripe_publishable_key || ""}
              onChange={(e) => setField("stripe_publishable_key", e.target.value)}
              placeholder="pk_live_… or pk_test_…"
              className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-500"
              data-testid="paygate-stripe-publishable-key"
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Secret Key</Label>
            <Input
              type={showSecrets ? "text" : "password"}
              value={cfg.stripe_secret_key || ""}
              onChange={(e) => setField("stripe_secret_key", e.target.value)}
              placeholder="sk_live_… or sk_test_…"
              className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-500"
              data-testid="paygate-stripe-secret-key"
            />
          </div>
        </CardContent>
      </Card>

      {/* PayFast (South Africa) */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-[#ED1C24] text-[10px] font-bold text-white">
                  PF
                </span>
                PayFast{" "}
                <span className="text-[10px] font-normal text-neutral-500">
                  (payfast.co.za · South Africa)
                </span>
              </CardTitle>
              <CardDescription className="text-xs text-neutral-500">
                Cards, Instant EFT, SnapScan, Zapper, Mobicred, Masterpass.
                Enable after filling in your PayFast merchant credentials.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="payfast_enabled" className="text-sm">
                Enabled
              </Label>
              <Switch
                id="payfast_enabled"
                checked={!!cfg.payfast_enabled}
                onCheckedChange={(v) => setField("payfast_enabled", !!v)}
                data-testid="paygate-payfast-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-xs text-neutral-400">Environment</Label>
              <Select
                value={cfg.payfast_environment || "sandbox"}
                onValueChange={(v) => setField("payfast_environment", v)}
              >
                <SelectTrigger
                  className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                  data-testid="paygate-payfast-env"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                  <SelectItem value="live">Live (production)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-neutral-400">Currency</Label>
              <Input
                value={cfg.payfast_currency || "ZAR"}
                onChange={(e) =>
                  setField("payfast_currency", e.target.value.toUpperCase())
                }
                placeholder="ZAR"
                className="border-white/10 bg-neutral-950 text-white placeholder:text-neutral-500"
                data-testid="paygate-payfast-currency"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-neutral-400">Merchant ID</Label>
            <Input
              value={cfg.payfast_merchant_id || ""}
              onChange={(e) => setField("payfast_merchant_id", e.target.value)}
              placeholder="10000100"
              className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-500"
              data-testid="paygate-payfast-merchant-id"
            />
          </div>

          <div>
            <Label className="text-xs text-neutral-400">Merchant Key</Label>
            <Input
              value={cfg.payfast_merchant_key || ""}
              onChange={(e) => setField("payfast_merchant_key", e.target.value)}
              placeholder="46f0cd694581a"
              className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-500"
              data-testid="paygate-payfast-merchant-key"
            />
          </div>

          <div>
            <Label className="text-xs text-neutral-400">
              Passphrase{" "}
              <span className="text-neutral-600">
                (optional but recommended)
              </span>
            </Label>
            <Input
              type={showSecrets ? "text" : "password"}
              value={cfg.payfast_passphrase || ""}
              onChange={(e) => setField("payfast_passphrase", e.target.value)}
              placeholder="Salted signature phrase"
              className="border-white/10 bg-neutral-950 font-mono text-xs text-white placeholder:text-neutral-500"
              data-testid="paygate-payfast-passphrase"
            />
          </div>

          <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-100/80">
            <p className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-red-400" />
              <span>
                Get credentials from{" "}
                <a
                  href="https://my.payfast.io/dashboard/account/integration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-300 underline hover:text-red-200"
                >
                  my.payfast.io → Settings → Integration
                </a>
                .{" "}
                <strong className="text-red-200">Match Environment to your credentials:</strong>{" "}
                Sandbox only accepts PayFast&apos;s shared test IDs{" "}
                <span className="font-mono">(10000100 / 46f0cd694581a)</span> —
                your <em>real</em> merchant ID from my.payfast.io must be paired
                with <em>Live</em> environment.
              </span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
